import React, { useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import QuestionCard from './QuestionCard';

/* ============================================================================
   LAYOUT type 2 — "Swipe": carrusel de tarjetas nativo para mobile. Una
   pregunta a la vez, ocupando toda la pantalla disponible, navegable
   arrastrando con el dedo (o mouse/lápiz vía Pointer Events) igual que un
   carrusel de stories o un stack tipo Tinder.

  
   ========================================================================== */

const SWIPE_DISTANCE_RATIO   = 0.22;
const SWIPE_VELOCITY_THRESHOLD = 0.5; // px/ms

const INTERACTIVE_SELECTOR =
  '.MuiSlider-root, .MuiRating-root, textarea, input, .radio-option, .emoji-item';

export const CardSwipeFormLayout = React.memo(({
  sortedQuestions,
  answers,
  currentStepIndex,
  unansweredQuestions,
  onRatingChange,
  onSliderChange,
  onSliderCommit,
  onTextChange,
  onRadioChange,
  onEmojiChange,
  onGoToStep,
  t,
}) => {
  const trackRef   = useRef(null);
  const dragRef    = useRef({ active: false, startX: 0, startTime: 0, width: 0, pointerId: null });
  const [dragX, setDragX]         = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const hasPrev = currentStepIndex > 0;
  const hasNext = currentStepIndex < sortedQuestions.length - 1;

  const handlePointerDown = useCallback((e) => {
    if (e.target.closest(INTERACTIVE_SELECTOR)) return;

    dragRef.current = {
      active:    true,
      startX:    e.clientX,
      startTime: performance.now(),
      width:     trackRef.current?.offsetWidth || 1,
      pointerId: e.pointerId,
    };
    setIsDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    let delta = e.clientX - dragRef.current.startX;
    if ((!hasPrev && delta > 0) || (!hasNext && delta < 0)) delta *= 0.3;
    setDragX(delta);
  }, [hasPrev, hasNext]);

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    const { startTime, width, pointerId } = dragRef.current;
    const elapsed  = Math.max(1, performance.now() - startTime);
    const velocity = Math.abs(dragX) / elapsed;
    const passedThreshold =
      Math.abs(dragX) > width * SWIPE_DISTANCE_RATIO || velocity > SWIPE_VELOCITY_THRESHOLD;

    dragRef.current.active = false;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture?.(pointerId);

    if (passedThreshold && dragX < 0 && hasNext) {
      onGoToStep(currentStepIndex + 1);
    } else if (passedThreshold && dragX > 0 && hasPrev) {
      onGoToStep(currentStepIndex - 1);
    }
    setDragX(0);
  }, [dragX, hasNext, hasPrev, currentStepIndex, onGoToStep]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight' && hasNext) {
      e.preventDefault();
      onGoToStep(currentStepIndex + 1);
    } else if (e.key === 'ArrowLeft' && hasPrev) {
      e.preventDefault();
      onGoToStep(currentStepIndex - 1);
    }
  }, [hasNext, hasPrev, currentStepIndex, onGoToStep]);

  return (
    <>
      {/* Puntos de progreso: los completados/actual son clicables para
          volver atrás; los futuros solo son indicadores visuales — avanzar
          de a un paso a la vez vía swipe/auto-avance mantiene la validación
          de SurveyResponse.jsx intacta (no permite saltarse preguntas
          requeridas). */}
      <Box className="swipe-dots" role="tablist" aria-label={t.progress || 'Progreso'}>
        {sortedQuestions.map(([qId], idx) => {
          const isPast = idx <= currentStepIndex;
          return (
            <Box
              key={qId}
              component={isPast ? 'button' : 'span'}
              type={isPast ? 'button' : undefined}
              role="tab"
              aria-selected={idx === currentStepIndex}
              aria-label={`${idx + 1}`}
              onClick={isPast ? () => onGoToStep(idx) : undefined}
              className={`swipe-dot${idx === currentStepIndex ? ' active' : ''}${idx < currentStepIndex ? ' done' : ''}`}
            />
          );
        })}
      </Box>

      <Box className="swipe-viewport-wrap">
        {hasPrev && (
          <Box
            component="button"
            type="button"
            aria-label={t.previousQuestion || 'Pregunta anterior'}
            className="swipe-arrow swipe-arrow-left"
            onClick={() => onGoToStep(currentStepIndex - 1)}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          </Box>
        )}

        <Box
          ref={trackRef}
          className="swipe-viewport"
          role="group"
          aria-roledescription="carousel"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <Box
            className="swipe-track"
            style={{
              transform:  `translateX(calc(${-currentStepIndex * 100}% + ${dragX}px))`,
              transition: isDragging ? 'none' : 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {sortedQuestions.map(([qId, question], idx) => (
              <Box key={qId} className="swipe-card-slot" aria-hidden={idx !== currentStepIndex}>
                <QuestionCard
                  qId={qId}
                  question={question}
                  answer={answers[qId]}
                  questionNumber={idx + 1}
                  onRatingChange={onRatingChange}
                  onSliderChange={onSliderChange}
                  onSliderCommit={onSliderCommit}
                  onTextChange={onTextChange}
                  onRadioChange={onRadioChange}
                  onEmojiChange={onEmojiChange}
                  showError={unansweredQuestions.has(qId)}
                  t={t}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {hasNext && (
          <Box
            component="button"
            type="button"
            aria-label={t.nextQuestion || 'Siguiente pregunta'}
            className="swipe-arrow swipe-arrow-right"
            onClick={() => onGoToStep(currentStepIndex + 1)}
          >
            <ChevronRightIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          </Box>
        )}
      </Box>
    </>
  );
});
CardSwipeFormLayout.displayName = 'CardSwipeFormLayout';

export default CardSwipeFormLayout;